import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { isOk, messageOf, sendGiftCard } from "../../../services/gift";
import { RECAPTCHA_SITE_KEY } from "../../../utils/recaptcha";
import PhoneInputField from "../../../components/Inputfields/phoneinput";

// How the card can be sent. Email comes from VerySoul; SMS / WhatsApp are
// sent through GoHighLevel, like the app's other texts.
const CHANNELS = [
  { key: "email", label: "Email" },
  { key: "sms", label: "SMS" },
  { key: "whatsapp", label: "WhatsApp" },
];

const ANONYMOUS = "Someone";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CHANNEL_LABELS = { email: "email", sms: "SMS", whatsapp: "WhatsApp" };

const formatWhen = (iso) =>
  new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

// "Send this gift" button, its dialog, and the list of past sends.
// `gift` needs: gift_id, code, status, sender_name_default, sent.
export default function GiftSendPanel({ gift }) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(gift.sent || []);

  if (gift.status !== "active") return null;

  return (
    <div className="vsr-send no-print">
      <div className="vsr-send-cta">
        <div>
          <h2 className="vsr-send-title">Send this gift</h2>
          <p className="vsr-muted">We'll send your recipient the Gift Code and a link to redeem it. Your name stays hidden unless you choose to show it.</p>
        </div>
        <button type="button" className="vsr-btn" onClick={() => setOpen(true)}>
          Send this gift
        </button>
      </div>

      {sent.length > 0 && (
        <ul className="vsr-send-history" aria-label="Sent so far">
          {sent.map((item, index) => (
            <li key={`${item.at}-${index}`}>
              <span className="vsr-send-dot" aria-hidden="true" />
              Sent by {CHANNEL_LABELS[item.channel] || item.channel} to <b>{item.to}</b>
              <span className="vsr-muted"> · {formatWhen(item.at)}</span>
            </li>
          ))}
        </ul>
      )}

      {open && (
        <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
          <SendDialog
            gift={gift}
            onClose={() => setOpen(false)}
            onSent={(history) => {
              setSent(history);
              setOpen(false);
            }}
          />
        </GoogleReCaptchaProvider>
      )}
    </div>
  );
}

function SendDialog({ gift, onClose, onSent }) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const firstField = useRef(null);
  const [channel, setChannel] = useState(CHANNELS[0].key);
  const [form, setForm] = useState({
    recipient_name: "",
    email: "",
    phone: "",
    show_name: false,
    sender_name: gift.sender_name_default || "",
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (firstField.current) firstField.current.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const update = (field, value) => {
    setForm({ ...form, [field]: value });
    if (errors[field] || errors.form) setErrors({ ...errors, [field]: "", form: "" });
  };

  const shownName = form.show_name && form.sender_name.trim() ? form.sender_name.trim() : ANONYMOUS;

  const submit = async (e) => {
    e.preventDefault();
    const found = {};
    if (channel === "email" && !EMAIL_PATTERN.test(form.email.trim())) found.email = "Please enter a valid email address.";
    // react-phone-input-2 gives digits including the country code, e.g. 14155550123.
    const phoneDigits = form.phone.replace(/\D/g, "");
    if (channel !== "email" && (phoneDigits.length < 8 || phoneDigits.length > 15)) {
      found.phone = "Please enter a valid mobile number, including the country code.";
    }
    if (form.show_name && !form.sender_name.trim()) found.sender_name = 'Enter the name to show, or untick "Show my name".';
    setErrors(found);
    if (Object.keys(found).length) return;

    setBusy(true);
    let recaptchaToken = "";
    try {
      recaptchaToken = executeRecaptcha ? await executeRecaptcha("gift_send") : "";
    } catch (err) {
      recaptchaToken = "";
    }
    const res = await sendGiftCard(gift.gift_id, {
      channel,
      email: channel === "email" ? form.email.trim() : "",
      phone: channel !== "email" ? `+${phoneDigits}` : "",
      recipient_name: form.recipient_name.trim(),
      show_name: form.show_name,
      sender_name: form.show_name ? form.sender_name.trim() : "",
      recaptcha_token: recaptchaToken,
    });
    setBusy(false);

    if (isOk(res)) {
      toast.success(`Your gift is on its way to ${res.data.data.sent.to}.`);
      onSent(res.data.data.history);
    } else {
      setErrors({ form: messageOf(res, "We couldn't send your gift. Please try again.") });
    }
  };

  return (
    <div className="vsr-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="vsr-modal" role="dialog" aria-modal="true" aria-labelledby="vsr-send-heading">
        <div className="vsr-modal-head">
          <h2 id="vsr-send-heading">Send this gift</h2>
          <button type="button" className="vsr-modal-close" aria-label="Close" onClick={onClose} disabled={busy}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        </div>

        <form className="vsr-modal-body" onSubmit={submit} noValidate>
          {CHANNELS.length > 1 && (
            <div className="vsr-field">
              <span className="vsr-label">Send by</span>
              <div className="vsr-segment" role="radiogroup" aria-label="Send by">
                {CHANNELS.map((item) => (
                  <button
                    type="button"
                    key={item.key}
                    role="radio"
                    aria-checked={channel === item.key}
                    className={`vsr-segment-btn ${channel === item.key ? "is-selected" : ""}`}
                    onClick={() => setChannel(item.key)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="vsr-form-grid">
            <div className="vsr-field">
              <label className="vsr-label" htmlFor="send-recipient-name">
                Recipient's name <span className="vsr-optional">(optional)</span>
              </label>
              <input
                id="send-recipient-name"
                ref={firstField}
                className="vsr-input"
                maxLength={60}
                value={form.recipient_name}
                onChange={(e) => update("recipient_name", e.target.value)}
              />
            </div>
            {channel === "email" && (
              <div className="vsr-field">
                <label className="vsr-label" htmlFor="send-email">
                  Recipient's email<i>*</i>
                </label>
                <input
                  id="send-email"
                  type="email"
                  autoComplete="off"
                  className={errors.email ? "vsr-input is-invalid" : "vsr-input"}
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                />
                {errors.email && <span className="vsr-error">{errors.email}</span>}
              </div>
            )}
            {channel !== "email" && (
              <div className="vsr-field">
                <label className="vsr-label" htmlFor="send-phone">
                  Recipient's mobile number<i>*</i>
                </label>
                <PhoneInputField
                  id="send-phone"
                  name="phone"
                  country="us"
                  className={`vsr-phone ${errors.phone ? "is-invalid" : ""}`}
                  value={form.phone}
                  setValue={(value) => update("phone", value)}
                />
                {errors.phone ? (
                  <span className="vsr-error">{errors.phone}</span>
                ) : (
                  <span className="vsr-hint">
                    {channel === "whatsapp" ? "The number they use for WhatsApp." : "A mobile number that can receive texts."}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="vsr-field">
            <label className="vsr-check">
              <input type="checkbox" checked={form.show_name} onChange={(e) => update("show_name", e.target.checked)} />
              <span>
                Show my name on the gift
                <span className="vsr-hint vsr-hint-inline">Otherwise it says "{ANONYMOUS} gifted you…"</span>
              </span>
            </label>
            {form.show_name && (
              <div className="vsr-field vsr-send-name">
                <label className="vsr-label" htmlFor="send-sender-name">
                  Name to show<i>*</i>
                </label>
                <input
                  id="send-sender-name"
                  className={errors.sender_name ? "vsr-input is-invalid" : "vsr-input"}
                  maxLength={40}
                  value={form.sender_name}
                  onChange={(e) => update("sender_name", e.target.value)}
                />
                {errors.sender_name && <span className="vsr-error">{errors.sender_name}</span>}
              </div>
            )}
          </div>

          {channel === "email" ? (
            <div className="vsr-preview" aria-live="polite">
              <p className="vsr-preview-label">Preview</p>
              <p className="vsr-preview-subject">{shownName} gifted you a VerySoul Experience!</p>
              <p>
                {shownName} gifted you a VerySoul Experience! Redeem your gift for a private session with a medium at VerySoul.
              </p>
              <p>
                Gift Code: <b>{gift.code}</b>
              </p>
              <span className="vsr-preview-btn">Redeem Here</span>
            </div>
          ) : (
            <div className="vsr-preview" aria-live="polite">
              <p className="vsr-preview-label">{channel === "whatsapp" ? "WhatsApp message" : "Text message"} preview</p>
              <p className={`vsr-bubble ${channel === "whatsapp" ? "is-whatsapp" : ""}`}>
                {shownName} gifted you a VerySoul Experience! Redeem your gift for a private session with a medium at VerySoul. Gift
                Code: {gift.code} Redeem Here: {gift.redeem_url}
              </p>
            </div>
          )}

          {errors.form && (
            <p className="vsr-error vsr-error-block" role="alert">
              {errors.form}
            </p>
          )}

          <div className="vsr-modal-actions">
            <button type="button" className="vsr-btn vsr-btn-outline" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="vsr-btn" disabled={busy}>
              {busy ? "Sending…" : "Send gift"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
